/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  HierarchicalMultiSelectWrapper,
  SelectButton,
  SelectedText,
  DropdownContainer,
  SelectPanel,
  SearchBar,
  BackButton,
  PanelList,
  PanelItem,
  ItemLabel,
  ItemArrow,
  AllItem,
  NoResults,
  IconWrapper,
  CheckIcon,
} from "./styles";
import {
  getAllLeafValues,
  getDescendantLeafValues,
  getAllLeafOptions,
  getDescendantLeafOptions,
} from "./options";
import { Input, IconSearch } from "tvs-unified-lib";
import { Control, useController } from "react-hook-form";

export interface Option {
  label: string;
  value: any;
  name?: any;
  children?: Option[];
}

interface HierarchicalMultiSelectProps {
  options: Option[];
  control: Control<any>;
  name: string;
  onChange?: (value: Option[]) => void;
  rootTitle?: string;
  label?: string;
  isRequired?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const GLOBAL_SELECT_ALL_VALUE = "___GLOBAL_ALL___";

const CheckMarkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const HierarchicalMultiSelect = ({
  options,
  control,
  name,
  onChange: onChangeProp,
  rootTitle = "All Items",
  label: formLabel = "",
  isRequired = false,
  placeholder = "Select...",
  className = "",
  disabled = false,
}: HierarchicalMultiSelectProps) => {
  const {
    field: { value: formValue = [], onChange: onChangeField },
    fieldState: { error },
  } = useController({ name, control });

  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentView, setCurrentView] = useState<{
    items: Option[];
    title: string | null;
  }>({
    items: options,
    title: null,
  });
  const [viewHistory, setViewHistory] = useState<
    Array<{ items: Option[]; title: string | null }>
  >([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setCurrentView({ items: options, title: null });
    setViewHistory([]);
    const validLeafValues = new Set(getAllLeafValues(options));
    const currentSelectedOptions = Array.isArray(formValue) ? formValue : [];

    const reconciledValue = currentSelectedOptions.filter((opt) =>
      validLeafValues.has(opt.value)
    );

    if (reconciledValue.length !== currentSelectedOptions.length) {
      onChangeField(reconciledValue);
      onChangeProp?.(reconciledValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      if (currentView.title !== null || viewHistory.length > 0) {
        setCurrentView({ items: options, title: null });
        setViewHistory([]);
      }
      setSearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, options]);

  const handleItemClick = useCallback(
    (item: Option) => {
      setViewHistory((prevHistory) => [...prevHistory, currentView]);
      setCurrentView({
        items: item.children || [],
        title: item.label,
      });
      setSearchTerm("");
    },
    [currentView]
  );

  const handleBackClick = useCallback(() => {
    if (viewHistory.length > 0) {
      const previousView = viewHistory[viewHistory.length - 1];
      setViewHistory((prevHistory) => prevHistory.slice(0, -1));
      setCurrentView(previousView);
      setSearchTerm("");
    }
  }, [viewHistory]);

  const handleCheckboxChange = useCallback(
    (itemValue: any, isChecked: boolean, item?: Option) => {
      const currentSelectedOptions = Array.isArray(formValue) ? formValue : [];
      let newSelectedOptions: Option[] = [...currentSelectedOptions];

      if (itemValue === GLOBAL_SELECT_ALL_VALUE) {
        if (isChecked) {
          newSelectedOptions = getAllLeafOptions(options);
        } else {
          newSelectedOptions = [];
        }
      } else if (item && item.children && item.children.length > 0) {
        const descendantOptions = getDescendantLeafOptions(item);
        const descendantValuesSet = new Set(
          descendantOptions.map((o) => o.value)
        );

        if (isChecked) {
          const currentValuesSet = new Set(
            currentSelectedOptions.map((o) => o.value)
          );
          const optionsToAdd = descendantOptions.filter(
            (opt) => !currentValuesSet.has(opt.value)
          );
          newSelectedOptions = [...currentSelectedOptions, ...optionsToAdd];
        } else {
          newSelectedOptions = currentSelectedOptions.filter(
            (opt) => !descendantValuesSet.has(opt.value)
          );
        }
      } else if (item) {
        if (isChecked) {
          if (!currentSelectedOptions.some((opt) => opt.value === item.value)) {
            newSelectedOptions.push(item);
          }
        } else {
          newSelectedOptions = currentSelectedOptions.filter(
            (opt) => opt.value !== item.value
          );
        }
      }

      onChangeField(newSelectedOptions);
      onChangeProp?.(newSelectedOptions);
    },
    [formValue, onChangeField, onChangeProp, options]
  );

  const allLeafOptions = useMemo(() => getAllLeafOptions(options), [options]);
  const allLeafValues = useMemo(
    () => allLeafOptions.map((o) => o.value),
    [allLeafOptions]
  );

  const isAllSelected = useMemo(() => {
    const currentSelectedOptions = Array.isArray(formValue) ? formValue : [];
    if (currentSelectedOptions.length === 0 || allLeafValues.length === 0)
      return false;
    if (currentSelectedOptions.length !== allLeafValues.length) return false;

    const currentSelectedValuesSet = new Set(
      currentSelectedOptions.map((opt) => opt.value)
    );
    return allLeafValues.every((leafValue) =>
      currentSelectedValuesSet.has(leafValue)
    );
  }, [formValue, allLeafValues]);

  const isIndeterminate = useMemo(() => {
    const currentLength = Array.isArray(formValue) ? formValue.length : 0;
    return currentLength > 0 && !isAllSelected;
  }, [formValue, isAllSelected]);

  const displayLabel = useMemo(() => {
    const currentSelectedOptions = Array.isArray(formValue) ? formValue : [];
    const selectedCount = currentSelectedOptions.length;

    if (selectedCount === 0) {
      return placeholder;
    }

    if (isAllSelected) {
      return rootTitle;
    }

    if (selectedCount > 3) {
      return `${selectedCount} selected`;
    }

    const labels = currentSelectedOptions.slice(0, 3).map((opt) => opt.label);
    return labels.join(", ") + (selectedCount > labels.length ? "..." : "");
  }, [formValue, placeholder, rootTitle, isAllSelected]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return currentView.items;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return currentView.items.filter((item) =>
      item.label.toLowerCase().includes(lowerSearchTerm)
    );
  }, [currentView.items, searchTerm]);

  return (
    <HierarchicalMultiSelectWrapper className={className} ref={wrapperRef}>
      {formLabel && (
        <label className="theme-form-input-label">
          {formLabel}{" "}
          {isRequired && <span className="color-theme-red-500">*</span>}{" "}
        </label>
      )}

      <SelectButton
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`${error ? "has-error" : ""} ${disabled ? "disabled" : ""}`}
        disabled={disabled}
      >
        <SelectedText>{displayLabel}</SelectedText>
        <IconWrapper>
          <svg
            fill="currentColor"
            viewBox="0 0 40 40"
            style={{
              transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <path d="M31 26.4q0 .3-.2.5l-1.1 1.2q-.3.2-.6.2t-.5-.2l-8.7-8.8-8.8 8.8q-.2.2-.5.2t-.5-.2l-1.2-1.2q-.2-.2-.2-.5t.2-.5l10.4-10.4q.3-.2.6-.2t.5.2l10.4 10.4q.2.2.2.5z"></path>
          </svg>
        </IconWrapper>
      </SelectButton>

      {isOpen && (
        <DropdownContainer role="listbox" aria-multiselectable="true">
          <SelectPanel $isNested={viewHistory.length > 0}>
            <SearchBar>
              <Input
                name={`${name}-search`}
                placeholder="Search"
                value={searchTerm}
                formLabel=""
                onInputChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                icon={<IconSearch />}
              />
            </SearchBar>

            {viewHistory.length > 0 && (
              <BackButton onClick={handleBackClick}>
                <svg
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.00391 12C8.01046 11.6725 8.12187 11.4039 8.39058 11.1485L13.3452 6.29476C13.5484 6.09825 13.7843 6 14.0727 6C14.6494 6 15.1278 6.47162 15.1278 7.04148C15.1278 7.32969 15.0033 7.59825 14.787 7.80786L10.4681 11.9934L14.787 16.1856C15.0033 16.4017 15.1278 16.6638 15.1278 16.9585C15.1278 17.5349 14.6494 18 14.0727 18C13.7843 18 13.5484 17.9017 13.3452 17.7052L8.39058 12.8515C8.12187 12.5895 8.00391 12.321 8.00391 12Z"
                    fill="#395278"
                  />
                </svg>
                {currentView.title}
              </BackButton>
            )}

            <PanelList $isNested={viewHistory.length > 0}>
              {viewHistory.length === 0 && !searchTerm && (
                <AllItem
                  $hasChildren={false}
                  role="option"
                  aria-selected={isAllSelected}
                >
                  <ItemLabel>
                    <input
                      type="checkbox"
                      aria-label={`Select all ${rootTitle}`}
                      checked={isAllSelected}
                      ref={(el) => el && (el.indeterminate = isIndeterminate)}
                      onChange={(e) =>
                        handleCheckboxChange(
                          GLOBAL_SELECT_ALL_VALUE,
                          e.target.checked
                        )
                      }
                    />
                    <span>{rootTitle}</span>
                  </ItemLabel>
                </AllItem>
              )}

              {filteredItems.map((item) => {
                const isLeaf = !item.children || item.children.length === 0;
                const currentSelectedOptions = Array.isArray(formValue)
                  ? formValue
                  : [];
                const currentSelectedValuesSet = new Set(
                  currentSelectedOptions.map((opt) => opt.value)
                );

                const isSelected = isLeaf
                  ? currentSelectedValuesSet.has(item.value)
                  : false;

                const descendantLeafValues = isLeaf
                  ? []
                  : getDescendantLeafValues(item);
                const areAllDescendantsSelected =
                  !isLeaf &&
                  descendantLeafValues.length > 0 &&
                  descendantLeafValues.every((leafValue) =>
                    currentSelectedValuesSet.has(leafValue)
                  );
                const areSomeDescendantsSelected =
                  !isLeaf &&
                  descendantLeafValues.some((leafValue) =>
                    currentSelectedValuesSet.has(leafValue)
                  );
                const isParentIndeterminate =
                  areSomeDescendantsSelected && !areAllDescendantsSelected;
                const parentCheckedState = areAllDescendantsSelected;

                return (
                  <PanelItem
                    key={item.value}
                    $hasChildren={!isLeaf}
                    role="option"
                    aria-selected={isLeaf ? isSelected : parentCheckedState}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isLeaf) {
                        handleCheckboxChange(item.value, !isSelected, item);
                      }
                    }}
                  >
                    <ItemLabel>
                      {isLeaf ? (
                        <CheckIcon checked={isSelected} aria-hidden="true">
                          {isSelected && <CheckMarkIcon />}
                        </CheckIcon>
                      ) : (
                        <input
                          type="checkbox"
                          aria-label={`Select all under ${item.label}`}
                          checked={parentCheckedState}
                          ref={(el) =>
                            el && (el.indeterminate = isParentIndeterminate)
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(
                              item.value,
                              e.target.checked,
                              item
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <span>{item.label}</span>
                    </ItemLabel>

                    {!isLeaf && (
                      <ItemArrow
                        aria-label={`View options under ${item.label}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemClick(item);
                        }}
                      >
                        <svg
                          width="25"
                          height="24"
                          viewBox="0 0 25 24"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.1086 12C16.1086 12.321 15.9972 12.5895 15.7219 12.8515L10.7673 17.7052C10.5707 17.9017 10.3282 18 10.0398 18C9.46308 18 8.99121 17.5349 8.99121 16.9585C8.99121 16.6638 9.10918 16.4017 9.32545 16.1856L13.6444 11.9934L9.32545 7.80786C9.10918 7.59825 8.99121 7.32969 8.99121 7.04148C8.99121 6.47162 9.46308 6 10.0398 6C10.3282 6 10.5707 6.09825 10.7673 6.29476L15.7219 11.1485C15.9906 11.4039 16.1086 11.6725 16.1086 12Z"
                            fill="#3548C9"
                          />
                        </svg>
                      </ItemArrow>
                    )}
                  </PanelItem>
                );
              })}

              {filteredItems.length === 0 && (
                <NoResults>No results found</NoResults>
              )}
            </PanelList>
          </SelectPanel>
        </DropdownContainer>
      )}
      {error && (
        <div
          role="alert"
          className="theme-font-sm color-theme-red-500 theme-form-error-message"
        >
          {error?.message}
        </div>
      )}
    </HierarchicalMultiSelectWrapper>
  );
};

export default HierarchicalMultiSelect;
